import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useScrollReveal, useScrollY } from '../lib/useScrollReveal';
import { useInstallPrompt } from '../lib/useInstallPrompt';

export function LandingPage() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  if (user === undefined) return null;
  if (user && !user.isAnonymous) return <Navigate to="/account" replace />;

  return (
    <div className="min-h-full flex flex-col bg-midnight text-stone-700 overflow-x-hidden">
      <Nav />
      <main className="flex-1">
        <Hero />
        <EventTicker />
        <FlowShowcase />
        <MultiDrive />
        <WhyDifferent />
        <PricingStrip />
        <BigCTA />
        <FAQ />
      </main>
      <Footer />
      <InstallPromptBar />
    </div>
  );
}

// Floating "Add to home screen" pill. Mobile only, dismissible, uses the
// same detection hook the guest page uses (Chrome native prompt for Android,
// manual Share-icon hint for iOS Safari).
function InstallPromptBar() {
  const install = useInstallPrompt();
  if (!install.mode) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 sm:hidden pointer-events-none pb-[env(safe-area-inset-bottom)]">
      <div className="mx-3 mb-3 pointer-events-auto">
        <div className="rounded-2xl bg-midnight/95 backdrop-blur border border-white/10 shadow-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-coral/20 text-coral grid place-items-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" />
              <path d="M7 8l5-5 5 5" />
              <path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-bone leading-tight">Install KeptShots</p>
            <p className="text-xs text-bone/60 leading-snug">
              {install.mode === 'ios'
                ? 'Tap the Share icon, then "Add to Home Screen".'
                : 'One-tap access — no app store.'}
            </p>
          </div>
          {install.mode === 'chrome' && (
            <button
              type="button"
              onClick={() => void install.promptInstall()}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-coral text-midnight hover:bg-coral-400 transition shrink-0"
            >
              Install
            </button>
          )}
          <button
            type="button"
            onClick={install.dismiss}
            aria-label="Dismiss"
            className="text-bone/50 hover:text-bone shrink-0 text-lg leading-none px-1"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Nav -------

function Nav() {
  const y = useScrollY();
  const scrolled = y > 20;
  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 transition-all duration-300 ${
        scrolled ? 'bg-midnight/85 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo dark />
          <span className="font-brand text-3xl tracking-tight text-bone drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">KeptShots</span>
        </Link>
        <div className="flex items-center gap-1">
          <Link to="/pricing" className="text-sm text-bone/85 hover:text-bone px-4 py-2 rounded-full transition drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] hidden sm:inline-flex">
            Pricing
          </Link>
          <Link to="/host/login" className="text-sm text-bone/85 hover:text-bone px-4 py-2 rounded-full transition drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
            Sign in
          </Link>
          <Link to="/host/login" className="text-sm px-4 py-2 rounded-full bg-coral text-midnight hover:bg-coral-400 transition font-medium shadow-[0_6px_20px_-6px_rgba(184,147,90,0.6)]">
            Get started
          </Link>
        </div>
      </div>
    </header>
  );
}

// ------- Hero — dark, with scrolling event photo collage behind -------

// Curated collage — Western/Catholic wedding + generic celebration textures only. No portraits that fight the type.
const COLLAGE_TOP = [
  'photo-1519741497674-611481863552', // confetti kiss
  'photo-1464366400600-7168b8af9bc3', // couple ceremony
  'photo-1511285560929-80b456fea0bc', // Catholic church interior
  'photo-1519225421980-715cb0215aed', // wedding rings
  'photo-1743684821666-05b9c5046937', // newlyweds pose
  'photo-1519671845924-1fd18db430b8', // confetti toss
  'photo-1502635385003-ee1e6a1a742d', // cheers glasses
  'photo-1554941829-202a0b2403b8', // hands cheers
  'photo-1523438885200-e635ba2c371e', // reception detail
  'photo-1520854221256-17451cc331bf', // celebration
];

const COLLAGE_BOTTOM = [
  'photo-1533105079780-92b9be482077', // wedding decor florals
  'photo-1727430256509-0f897d6f4765', // couple under floral canopy
  'photo-1543007630-9710e4a00a20', // wedding flowers close
  'photo-1519671482749-fd09be7ccebf', // sparkler celebration
  'photo-1470753937643-efeb931202a9', // birthday cake candles
  'photo-1735052712464-9d24b69be5f5', // bride and groom in woods
  'photo-1622726699405-29ba7469fd41', // birthday candles close
  'photo-1533561797500-4fad4750814e', // wedding reception
  'photo-1550005809-91ad75fb315f', // birthday balloons
  'photo-1511578314322-379afb476865', // event crowd
];

function imgUrl(id: string, w = 500) {
  return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;
}

function Hero() {
  return (
    <section className="relative min-h-[100svh] bg-midnight text-bone overflow-hidden flex items-stretch">
      {/* Layer 1: scrolling photo collage — top mask fades photos in from the nav; bottom is unclipped since ticker is also dark */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          maskImage:
            'linear-gradient(to bottom, black 0%, black 92%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, black 0%, black 92%, transparent 100%)',
        }}
      >
        <div className="absolute inset-0 flex flex-col gap-2">
          <PhotoRow photos={COLLAGE_TOP} direction="left" duration={160} />
          <PhotoRow photos={COLLAGE_BOTTOM} direction="right" duration={190} />
        </div>
      </div>

      {/* Layer 2: heavy central scrim — photos read as texture at the edges, type sits on solid dark */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 90% 70% at 50% 50%, rgba(17,17,17,0.94) 0%, rgba(17,17,17,0.85) 45%, rgba(17,17,17,0.6) 75%, rgba(17,17,17,0.35) 100%)',
        }}
      />

      {/* Layer 3: warm gold aura behind headline */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(700px 500px at 50% 45%, rgba(184,147,90,0.18), transparent 65%)',
        }}
      />

      {/* Content — vertically centered */}
      <div className="relative z-10 w-full min-h-[100svh] flex flex-col items-center justify-center text-center px-6 pt-20 pb-16 gap-8 sm:gap-10">
        <p
          className="text-[0.7rem] sm:text-xs uppercase tracking-[0.4em] text-coral-300/85 animate-rise-in drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)] flex items-center gap-3 sm:gap-4"
          style={{ animationDelay: '60ms' }}
        >
          <span>You host</span>
          <span className="w-1 h-1 rounded-full bg-coral-300/60" />
          <span>Guests scan</span>
          <span className="w-1 h-1 rounded-full bg-coral-300/60" />
          <span>Photos land</span>
        </p>

        <h1
          className="font-serif leading-[0.95] tracking-tight animate-rise-in w-full"
          style={{
            fontSize: 'clamp(3rem, 9vw, 7.5rem)',
            animationDelay: '120ms',
          }}
        >
          <span className="block text-bone drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">Every guest photo,</span>
          <span className="block italic text-coral-300 drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">in your Drive.</span>
        </h1>

        <p
          className="max-w-xl text-base sm:text-lg text-bone/75 leading-relaxed animate-rise-in drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)]"
          style={{ animationDelay: '240ms' }}
        >
          Every guest becomes a photographer. Every photo becomes yours.
        </p>

        <div
          className="flex flex-wrap items-center justify-center gap-3 animate-rise-in"
          style={{ animationDelay: '360ms' }}
        >
          <Link
            to="/host/login"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-coral text-midnight font-medium hover:bg-coral-400 transition text-base shadow-[0_20px_50px_-15px_rgba(184,147,90,0.6)]"
          >
            <span>Start hosting</span>
            <span>→</span>
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-bone hover:bg-white/10 hover:border-white/25 transition font-medium text-base"
          >
            See how it works
          </a>
        </div>

        <p
          className="text-xs text-bone/50 tracking-wide animate-rise-in flex items-center gap-2 sm:gap-3 flex-wrap justify-center"
          style={{ animationDelay: '480ms' }}
        >
          <span>60-second setup</span>
          <span className="w-1 h-1 rounded-full bg-bone/25" />
          <span>Free to start</span>
          <span className="w-1 h-1 rounded-full bg-bone/25" />
          <span>Your Drive, always</span>
        </p>
      </div>

      <style>{`
        @keyframes marquee-left { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes marquee-right { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(6px); opacity: 1; } }
        .animate-bounce-slow { animation: bounce-slow 2.4s ease-in-out infinite; }
      `}</style>
    </section>
  );
}

function PhotoRow({ photos, direction, duration }: { photos: string[]; direction: 'left' | 'right'; duration: number }) {
  const doubled = [...photos, ...photos];
  return (
    <div
      className="flex gap-2 w-max flex-1"
      style={{ animation: `marquee-${direction} ${duration}s linear infinite` }}
    >
      {doubled.map((id, i) => (
        <PhotoTile key={`${id}-${i}`} id={id} eager={i < 4} />
      ))}
    </div>
  );
}

function PhotoTile({ id, eager }: { id: string; eager: boolean }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return (
    <div className="w-52 sm:w-64 md:w-72 lg:w-80 shrink-0 h-full overflow-hidden bg-midnight-800">
      <img
        src={imgUrl(id, 500)}
        alt=""
        loading={eager ? 'eager' : 'lazy'}
        onError={() => setOk(false)}
        className="w-full h-full object-cover"
        style={{ filter: 'saturate(0.35) brightness(0.75) contrast(1.05)' }}
      />
    </div>
  );
}

// ------- Event ticker (single-line scrolling names) -------

function EventTicker() {
  const events = ['Weddings', 'Birthdays', 'Baby showers', 'Anniversaries', 'Reunions', 'Engagements', 'Christenings', 'Graduations', 'Retirements', 'Housewarmings'];
  const doubled = [...events, ...events, ...events];
  return (
    <section className="relative overflow-hidden py-6 sm:py-10 bg-midnight">
      <div
        className="flex items-center gap-6 sm:gap-10 whitespace-nowrap font-serif text-xl sm:text-3xl md:text-4xl text-bone/45 w-max"
        style={{ animation: 'ticker 40s linear infinite' }}
      >
        {doubled.map((w, i) => (
          <span key={i} className="flex items-center gap-6 sm:gap-10 shrink-0">
            {w}
            <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-coral shrink-0" />
          </span>
        ))}
      </div>
      <style>{`@keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }`}</style>
    </section>
  );
}

// ------- Flow showcase — a visual, illustrated walkthrough. Each row is a
// vignette (poster / phone / Drive) paired with a copy block. The illustrations
// are hand-composed with DOM + Tailwind, not stock images — this is the piece
// that makes the page feel bespoke instead of templated.

function FlowShowcase() {
  const [ref, visible] = useScrollReveal<HTMLDivElement>(0.15);
  return (
    <section id="how-it-works" ref={ref} className="bg-cream py-24 sm:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 space-y-20 sm:space-y-28">
        <div
          className={`text-center max-w-2xl mx-auto space-y-3 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.24em] text-stone-400">The whole thing</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-stone-900">
            You set it up once. <span className="italic text-coral-600">Then it just happens.</span>
          </h2>
        </div>

        <FlowRow
          n="01"
          eyebrow="Set up"
          title="Connect Drive. Get a QR poster."
          body="Sign in with Google, pick your event date, print the poster. That's the whole setup — less than a minute."
          visual={<PosterVisual />}
        />
        <FlowRow
          n="02"
          eyebrow="On the day"
          title="Guests scan. Upload from their phone."
          body="No app to download. No account. They tap 'send' and full-resolution photos land in your folder as they go."
          visual={<PhoneVisual />}
          reverse
        />
        <FlowRow
          n="03"
          eyebrow="Forever after"
          title="Every photo, in your Drive."
          body="Cancel KeptShots any day. Your photos never move. They were always yours."
          visual={<DriveVisual />}
        />
      </div>
    </section>
  );
}

function FlowRow({
  n,
  eyebrow,
  title,
  body,
  visual,
  reverse = false,
}: {
  n: string;
  eyebrow: string;
  title: string;
  body: string;
  visual: React.ReactNode;
  reverse?: boolean;
}) {
  const [ref, visible] = useScrollReveal<HTMLDivElement>(0.15);
  return (
    <div
      ref={ref}
      className={`grid md:grid-cols-2 gap-10 sm:gap-16 items-center ${reverse ? 'md:[direction:rtl]' : ''}`}
    >
      <div
        className={`md:[direction:ltr] space-y-5 transition-all duration-700 ${
          visible ? 'opacity-100 translate-x-0' : `opacity-0 ${reverse ? 'translate-x-6' : '-translate-x-6'}`
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="font-serif text-3xl italic text-coral">{n}</span>
          <span className="text-xs uppercase tracking-[0.24em] text-stone-400">{eyebrow}</span>
        </div>
        <h3 className="font-serif text-3xl sm:text-4xl leading-[1.1] tracking-tight text-stone-900">{title}</h3>
        <p className="text-stone-500 text-lg leading-relaxed max-w-md">{body}</p>
      </div>
      <div
        className={`md:[direction:ltr] flex justify-center transition-all duration-700 delay-150 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        {visual}
      </div>
    </div>
  );
}

// ---- Illustrations. All CSS/DOM — no external assets ----

function PosterVisual() {
  return (
    <div className="relative w-full max-w-sm">
      <div
        className="relative bg-bone rounded-lg p-6 sm:p-8 shadow-card border border-stone-100"
        style={{ transform: 'rotate(-1.5deg)' }}
      >
        <p className="text-[0.6rem] uppercase tracking-[0.3em] text-stone-400 text-center mb-3">A KeptShots event</p>
        <p className="font-serif text-2xl text-center text-stone-900 leading-tight">Sam & Aditi's Wedding</p>
        <p className="italic text-stone-400 text-sm text-center mt-1">Scan to send us your photos</p>
        <div className="mx-auto my-5 aspect-square w-40 bg-white border border-stone-200 rounded-md grid place-items-center overflow-hidden">
          <QRArt />
        </div>
        <p className="text-center text-xs text-stone-400">keptshots.com/event/sam-aditi</p>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-stone-200/60 blur-[3px] rounded-full" />
      </div>
      <div
        className="absolute -bottom-3 -right-4 bg-coral-500 text-midnight rounded-full px-3 py-1 text-xs font-medium shadow-[0_10px_25px_-8px_rgba(184,147,90,0.7)]"
        style={{ transform: 'rotate(6deg)' }}
      >
        Print or project it
      </div>
    </div>
  );
}

function QRArt() {
  // A stylized QR-look grid — not a real QR, just decorative pixel art.
  const cells = Array.from({ length: 11 * 11 }, (_, i) => {
    const x = i % 11;
    const y = Math.floor(i / 11);
    // finder squares in three corners
    const finder = (x < 3 && y < 3) || (x > 7 && y < 3) || (x < 3 && y > 7);
    const finderInner = (x === 1 && y === 1) || (x === 9 && y === 1) || (x === 1 && y === 9);
    const filled = finder ? !((x === 1 || x === 9 || x === 1) && (y === 1 || y === 1 || y === 9)) || finderInner : ((x * 3 + y * 7 + x * y) % 3 === 0);
    return filled;
  });
  return (
    <div className="grid grid-cols-11 gap-[1.5px] w-32 h-32 p-1">
      {cells.map((on, i) => (
        <div key={i} className={on ? 'bg-stone-900 rounded-[1px]' : 'bg-transparent'} />
      ))}
    </div>
  );
}

function PhoneVisual() {
  return (
    <div className="relative">
      <div
        className="relative bg-stone-900 rounded-[2.4rem] p-2 shadow-soft border border-stone-800"
        style={{ width: 240, transform: 'rotate(3deg)' }}
      >
        <div className="bg-cream rounded-[2rem] overflow-hidden aspect-[9/19]">
          {/* status bar */}
          <div className="h-6 bg-cream flex items-center justify-between px-5 text-[0.55rem] text-stone-400">
            <span>9:41</span>
            <span>●●●</span>
          </div>
          {/* app screen */}
          <div className="px-4 pt-2 pb-4 space-y-3">
            <div className="text-center">
              <p className="text-[0.5rem] uppercase tracking-[0.3em] text-stone-400">Sam & Aditi</p>
              <p className="font-serif text-lg text-stone-900 leading-tight">Send a photo</p>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="aspect-square rounded-md bg-stone-100 relative overflow-hidden">
                  {i < 4 && (
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-coral-200 to-coral-500"
                      style={{ opacity: 0.3 + i * 0.15 }}
                    />
                  )}
                  {i < 3 && (
                    <div className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-white grid place-items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-coral-600" />
                    </div>
                  )}
                  {i === 3 && (
                    <div className="absolute inset-0 grid place-items-center">
                      <div className="w-6 h-6 rounded-full border-2 border-coral-500 border-t-transparent animate-spin" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="rounded-full bg-stone-900 text-cream text-[0.65rem] py-2 text-center font-medium">
              4 photos sent · 2 more going
            </div>
            <div className="flex items-center gap-2 justify-center text-[0.55rem] text-stone-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Straight to Sam & Aditi's Drive</span>
            </div>
          </div>
        </div>
      </div>
      {/* upward-arrow float, "photo lifting off" */}
      <div className="absolute -top-6 -right-6 flex flex-col items-center gap-0.5 text-coral-500 text-xs">
        <span>↑</span>
        <span>↑</span>
        <span className="opacity-40">↑</span>
      </div>
    </div>
  );
}

function DriveVisual() {
  const files = [
    { name: 'IMG_0421.HEIC', time: '3s ago' },
    { name: 'IMG_0422.HEIC', time: '5s ago' },
    { name: 'IMG_0417.HEIC', time: '18s ago' },
    { name: 'IMG_0410.HEIC', time: '32s ago' },
    { name: 'IMG_0405.HEIC', time: '1m ago' },
  ];
  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-xl shadow-card border border-stone-100 overflow-hidden">
        <div className="border-b border-stone-100 px-4 py-3 flex items-center gap-2 text-xs text-stone-500">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="ml-3 truncate">drive.google.com / Sam & Aditi Wedding</span>
        </div>
        <div className="p-5 space-y-2.5">
          <div className="flex items-center gap-2 text-sm text-stone-500 pb-2 border-b border-stone-100">
            <FolderIcon />
            <span className="font-medium text-stone-800">Sam & Aditi Wedding</span>
            <span className="ml-auto text-xs text-emerald-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              live
            </span>
          </div>
          {files.map((f, i) => (
            <div
              key={f.name}
              className="flex items-center gap-3 py-1.5 text-sm hover:bg-stone-50 rounded transition"
              style={{ opacity: 1 - i * 0.12 }}
            >
              <div className="w-8 h-8 rounded bg-gradient-to-br from-coral-100 to-coral-300 grid place-items-center shrink-0">
                <PhotoIcon />
              </div>
              <span className="text-stone-700 truncate flex-1">{f.name}</span>
              <span className="text-xs text-stone-400 shrink-0">{f.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FolderIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-stone-500">
      <path
        d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function PhotoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-coral-700">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.5" fill="currentColor" />
      <path d="M4 18l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

// ------- Multi-Drive — the "run out of space?" answer -------

function MultiDrive() {
  const [ref, visible] = useScrollReveal<HTMLDivElement>();
  const accounts = [
    { email: 'sam@gmail.com', used: '12.4 GB', total: '15 GB', color: 'from-emerald-100 to-emerald-200', ring: 'text-emerald-600' },
    { email: 'aditi@gmail.com', used: '3.1 GB', total: '15 GB', color: 'from-sky-100 to-sky-200', ring: 'text-sky-600' },
    { email: 'events@family.com', used: '0 GB', total: '2 TB', color: 'from-coral-100 to-coral-200', ring: 'text-coral-700' },
  ];
  return (
    <section ref={ref} className="bg-cream py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
        <div
          className={`space-y-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.24em] text-coral-700">Never run out of space</p>
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] tracking-tight text-stone-900">
            Connect <span className="italic text-coral-700">multiple</span> Google Drives.
          </h2>
          <p className="text-stone-500 text-lg leading-relaxed max-w-md">
            Every Google account comes with <span className="text-stone-800 font-medium">15 GB free</span> — enough
            for a small event on its own. A four-day wedding with video? Add your partner's Gmail, a spare account,
            or a Workspace Drive. We fail over automatically the moment one fills up — guests never see an error.
          </p>
          <ul className="space-y-2 text-sm text-stone-600 max-w-md">
            <li className="flex gap-3">
              <span className="w-1 h-1 rounded-full bg-coral-600 mt-2 shrink-0" />
              <span>Add or remove accounts any time from the dashboard.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-1 h-1 rounded-full bg-coral-600 mt-2 shrink-0" />
              <span>Photos route to whichever connected Drive has room.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-1 h-1 rounded-full bg-coral-600 mt-2 shrink-0" />
              <span>Nothing ever touches our servers — the bytes go straight from guest phone to your Drive.</span>
            </li>
          </ul>
        </div>
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '150ms' }}
        >
          <div className="bg-white rounded-2xl shadow-card border border-stone-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-stone-400">Connected Drives</p>
                <p className="font-medium text-stone-800 text-sm mt-0.5">3 accounts · 2.02 TB free</p>
              </div>
              <span className="text-xs text-emerald-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Failover on
              </span>
            </div>
            <div className="p-5 space-y-3">
              {accounts.map((a, i) => (
                <div
                  key={a.email}
                  className="flex items-center gap-3 p-3 rounded-xl border border-stone-100 bg-stone-50/60"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${a.color} grid place-items-center shrink-0 ${a.ring}`}>
                    <DriveGlyph />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800 truncate">{a.email}</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {a.used} of {a.total} used
                    </p>
                  </div>
                  <span className="text-[0.65rem] uppercase tracking-[0.18em] text-stone-400 shrink-0">
                    Slot {i + 1}
                  </span>
                </div>
              ))}
              <button
                type="button"
                className="w-full text-sm text-stone-500 hover:text-coral-700 border border-dashed border-stone-200 rounded-xl py-3 transition"
                disabled
              >
                + Add another Drive
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DriveGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3h8l6 10-4 8H6l-4-8z" />
      <path d="M8 3l4 10h10" />
      <path d="M12 13L6 21" />
    </svg>
  );
}

// ------- Pricing strip — scarcity CTA, right where people ask -------

function PricingStrip() {
  const [ref, visible] = useScrollReveal<HTMLDivElement>();
  return (
    <section ref={ref} className="bg-cream py-16">
      <div
        className={`max-w-3xl mx-auto px-6 text-center transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 px-6 sm:px-8 py-5 rounded-3xl bg-white border border-stone-200 shadow-card">
          <div className="text-center sm:text-left">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-coral-700 mb-1">Launch offer</p>
            <p className="font-serif text-2xl sm:text-3xl text-stone-900 leading-tight">
              <span className="text-stone-400 line-through mr-2 text-xl sm:text-2xl">₹99</span>
              Free per event, <span className="italic text-coral-700">for now.</span>
            </p>
            <p className="text-sm text-stone-500 mt-1">No card. No catch. Lock in your rate today.</p>
          </div>
          <Link
            to="/host/login"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-cream text-sm font-medium hover:bg-stone-800 transition"
          >
            Host your event now <span>→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ------- Why different (dark section, contrast break) -------

function WhyDifferent() {
  const [ref, visible] = useScrollReveal<HTMLDivElement>();
  const points = [
    { title: 'Photos live in your Drive', body: 'Not on our servers. Cancel this app any day and keep every photo.' },
    { title: 'No expiring gallery', body: "Other apps charge monthly to keep your photos alive. We don't hold them at all." },
    { title: 'Full resolution, always', body: 'No compression from us. What your guest took is what lands in your Drive.' },
  ];
  return (
    <section ref={ref} className="bg-midnight text-bone py-24 sm:py-32 relative">
      <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-start">
        <div className={`space-y-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-xs uppercase tracking-[0.24em] text-coral-300">Why we exist</p>
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] tracking-tight text-bone">
            Every other app holds your photos <span className="italic text-coral-400">hostage.</span>
          </h2>
          <p className="text-bone/60 text-lg leading-relaxed max-w-md">
            Guest-photo apps make money by keeping your memories on their servers, then charging you to keep access.
            That is not our business model. We help you collect, then we get out of your way.
          </p>
        </div>
        <div className="space-y-px bg-white/10 rounded-2xl overflow-hidden">
          {points.map((p, i) => (
            <div
              key={p.title}
              className={`bg-midnight p-6 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${i * 100 + 100}ms` }}
            >
              <h3 className="font-medium text-bone mb-1.5">{p.title}</h3>
              <p className="text-sm text-bone/55 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------- Big CTA -------

function BigCTA() {
  const [ref, visible] = useScrollReveal<HTMLDivElement>();
  return (
    <section ref={ref} className="bg-cream py-24 sm:py-32">
      <div
        className={`max-w-3xl mx-auto px-6 text-center space-y-8 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] tracking-tight text-stone-900">
          Your event, <span className="italic text-coral-600">your photos.</span>
        </h2>
        <p className="text-stone-500 text-lg max-w-xl mx-auto leading-relaxed">
          Set up in a minute. Free while we're getting started. Cancel any time and keep every photo forever.
        </p>
        <Link
          to="/host/login"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-stone-900 text-cream font-medium hover:bg-stone-800 transition shadow-soft text-lg"
        >
          Start hosting <span>→</span>
        </Link>
      </div>
    </section>
  );
}

// ------- FAQ -------

function FAQ() {
  const items = [
    { q: 'Do guests need an account?', a: 'No. They open the link, upload, done. Optional nickname if they want their name on their photos.' },
    { q: 'What kinds of events work?', a: 'Anything with guests and cameras. Weddings, birthdays, corporate events, reunions, baby showers, product launches.' },
    { q: 'How much Drive space do I need?', a: '15 GB free comes with every Google account — enough for around 3,000 photos. Running low? Connect a second Google account (partner\'s, spare Gmail, Workspace Drive) and we\'ll fail over automatically. Video-heavy events, upgrade Drive storage or add another account.' },
    { q: 'What if I lose internet during my event?', a: 'Guest phones queue photos locally and send them the moment signal returns. Even if the browser closes mid-upload.' },
    { q: 'Do you see my photos?', a: 'We generate a small thumbnail for the live gallery. The original file goes directly from the guest browser to your Drive — we never handle it.' },
  ];
  const [ref, visible] = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-cream pb-24 sm:pb-32">
      <div className="max-w-2xl mx-auto px-6 space-y-10">
        <div className="text-center space-y-3">
          <p className="text-xs uppercase tracking-[0.24em] text-stone-400">Questions</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900">Good ones, honestly answered.</h2>
        </div>
        <div ref={ref} className="divide-y divide-stone-200 border-y border-stone-200">
          {items.map((it, i) => (
            <details
              key={it.q}
              className={`group transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <summary className="cursor-pointer list-none py-5 flex items-center justify-between gap-4">
                <span className="font-medium text-stone-800">{it.q}</span>
                <span className="text-coral-600 group-open:rotate-45 transition-transform text-2xl leading-none font-light">+</span>
              </summary>
              <div className="pb-5 text-stone-500 leading-relaxed">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------- Footer -------

function Footer() {
  return (
    <footer className="bg-midnight text-bone/60">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap gap-4 items-center justify-between text-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          <Logo dark />
          <span className="font-brand text-2xl text-bone">KeptShots</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/pricing" className="hover:text-bone transition">Pricing</Link>
          <Link to="/privacy" className="hover:text-bone transition">Privacy</Link>
          <Link to="/terms" className="hover:text-bone transition">Terms</Link>
          <Link to="/host/login" className="hover:text-bone transition">Sign in</Link>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-bone/40">
          <span>
            Made by{' '}
            <a
              href="https://fletcherfernandes.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-bone/60 hover:text-coral-400 transition underline-offset-4 hover:underline"
            >
              fletcherfernandes.com
            </a>
          </span>
          <span className="w-1 h-1 rounded-full bg-bone/25" />
          <span className="inline-flex items-center gap-1.5">
            Built in India
            <span
              aria-hidden
              className="inline-flex flex-col leading-none rounded-[1px] overflow-hidden border border-white/10"
              style={{ width: 22 }}
            >
              <span className="h-[5px] bg-[#ff9933]" />
              <span className="h-[5px] bg-white grid place-items-center">
                <span
                  className="rounded-full border border-[#000080]"
                  style={{ width: 4, height: 4, boxSizing: 'border-box' }}
                />
              </span>
              <span className="h-[5px] bg-[#138808]" />
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
}

// ------- Bits -------

function Logo({ dark = false }: { dark?: boolean }) {
  // On dark surfaces (nav over the hero), place the cream-backed PNG on a
  // matching warm chip so it doesn't float. On light surfaces the PNG's own
  // cream bg blends naturally.
  return (
    <div className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 overflow-hidden ${dark ? 'bg-bone' : ''}`}>
      <img src="/logo-64.png" srcSet="/logo-64.png 1x, /logo-192.png 3x" width={28} height={28} alt="KeptShots" className="w-full h-full object-contain" />
    </div>
  );
}

